import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { applyImportNext } from './importNext';
import { uploadBookId } from '../../utils/uploadBookId';

const ImportUploadModal = React.lazy(() => import('./ImportUploadModal'));

class UploadModalBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onFail?.();
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/**
 * Opens the existing upload dialog on the new book's page.
 * All stories never hosts it. Closing, or a render failure, leaves
 * the writer in the book so Upload a doc still opens the same dialog.
 */
export default function BookUploadDoor() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentProject, currentStory, currentSession } = useApp();
  const [open, setOpen] = useState(false);
  const [storyId, setStoryId] = useState(null);
  const asked = location.state?.openUpload === true;
  const onAllStories = location.pathname === '/projects' || location.pathname === '/projects/';
  const projectId = location.state?.projectId || null;
  const projectReady = !projectId || currentProject?.id === projectId;

  useEffect(() => {
    if (!asked || onAllStories || !projectReady || !currentProject?.id) return undefined;
    setStoryId(location.state?.storyId || currentProject.story_id || null);
    setOpen(true);
    const nextState = { ...(location.state || {}) };
    delete nextState.openUpload;
    navigate(`${location.pathname}${location.search || ''}`, {
      replace: true,
      state: nextState,
    });
    return undefined;
  }, [
    asked,
    onAllStories,
    projectReady,
    currentProject?.id,
    currentProject?.story_id,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  if (!open || !currentProject?.id) return null;
  const bookId = storyId
    || uploadBookId({
      pathname: location.pathname,
      currentStory,
      currentSession,
    })
    || currentProject.story_id
    || null;

  const close = (payload) => {
    setOpen(false);
    if (payload?.next) {
      applyImportNext(navigate, payload, currentProject, {
        currentStoryId: payload.story_id || bookId,
      });
    }
  };

  return (
    <UploadModalBoundary onFail={() => setOpen(false)}>
      <React.Suspense fallback={null}>
        <ImportUploadModal
          project={currentProject}
          storyId={bookId}
          showPromptChooser
          onClose={close}
        />
      </React.Suspense>
    </UploadModalBoundary>
  );
}
